import { TwoPointDragger } from "./TwoPointDragger";
import { trackShape } from "@/analytics/drawing-analytics";
import {
  DraggerType,
  type Point2D,
  type Point3D,
  type TypedInputSpec,
} from "./types";
import { deleteObj } from "../core/delete";
import { getEntityHandle } from "../core/oda";
import { AddEntitiesCommand } from "../commands/AddEntitiesCommand";
import { PREVIEW_LINE_STYLE } from "./previewStyles";
import { MeasurementLabels } from "../overlay/MeasurementLabels";
import { DEFAULT_TEXT_STYLE } from "../database/textStyle";
import {
  createDimension,
  defaultDimLinePoint,
  dimensionLineEnds,
  formatDimensionValue,
  measuredDistance,
  DEFAULT_OFFSET_IN_TEXT_HEIGHTS,
} from "../dimensions";

/** Below this, the two points measure nothing along the chosen axis. */
const MIN_MEASURE = 1e-9;

/**
 * Linear dimension tool: press-drag-release fixes the first point, a second
 * press-drag-release fixes the second one (snap applies to both), and the
 * dimension is created right away above them. It is then selected, so its
 * dimension-line handle is under the stylus to place it.
 *
 * What it measures (X, Y or true distance) is viewer.dimensionKind. Its size
 * follows the active scale: the text height of the active note configuration,
 * which the scale menu sets (forced size included), becomes the DIMSCALE.
 */
export class DimensionDragger extends TwoPointDragger {
  readonly type = DraggerType.Dimension;

  /** Extension lines then dimension line. */
  private previewLineIds: string[] = [];
  private measureLabels: MeasurementLabels | null = null;

  /** Text height of the active scale, in drawing units. */
  private get textHeight(): number {
    const props = this.viewer.activeNoteConfig.props;
    return (
      props.textsize ?? props.textstyle?.size ?? DEFAULT_TEXT_STYLE.size ?? 1
    );
  }

  private get offset(): number {
    return this.textHeight * DEFAULT_OFFSET_IN_TEXT_HEIGHTS;
  }

  // ============== Preview ==============

  protected createPreview(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    for (let i = 0; i < 3; i++) {
      const id = overlay.createLine(
        screen.x,
        screen.y,
        screen.x,
        screen.y,
        PREVIEW_LINE_STYLE,
      );
      if (id) {
        this.previewLineIds.push(id);
        this.addPreviewId(id);
      }
    }
  }

  protected updatePreview(_screen: Point2D, world: Point3D): void {
    const overlay = this.viewer.overlayManager;
    const a = this.getSnapReferencePoint();
    if (!overlay || !a || this.previewLineIds.length !== 3) return;

    const kind = this.viewer.dimensionKind;
    const dimLinePoint = defaultDimLinePoint(kind, a, world, this.offset);
    const [start, end] = dimensionLineEnds(kind, a, world, dimLinePoint);

    const toScreen = (p: Point3D) => this.viewer.worldToScreen(p);
    const segments: [Point2D, Point2D][] = [
      [toScreen(a), toScreen(start)],
      [toScreen(world), toScreen(end)],
      [toScreen(start), toScreen(end)],
    ];
    segments.forEach(([from, to], i) => {
      overlay.update(this.previewLineIds[i], {
        x1: from.x,
        y1: from.y,
        x2: to.x,
        y2: to.y,
      });
    });

    this.updateLabel(segments[2], measuredDistance(kind, a, world));
  }

  private updateLabel([from, to]: [Point2D, Point2D], value: number): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    if (!this.measureLabels) {
      this.measureLabels = new MeasurementLabels(
        overlay,
        this.viewer.getBackgroundColor(),
      );
    }
    // Just above the dimension line on screen, where the text will be.
    this.measureLabels.setLabel(
      "length",
      (from.x + to.x) / 2,
      (from.y + to.y) / 2 - 14,
      formatDimensionValue(value),
    );
  }

  // ============== Entity Creation ==============

  protected createEntity(a: Point3D, b: Point3D): void {
    const db = this.viewer.database;
    if (!db) return;

    const kind = this.viewer.dimensionKind;
    if (measuredDistance(kind, a, b) < MIN_MEASURE) return;

    const config = this.viewer.activeNoteConfig;
    const entityId = createDimension(this.module, db, {
      kind,
      a,
      b,
      dimLinePoint: defaultDimLinePoint(kind, a, b, this.offset),
      textHeight: this.textHeight,
      textstyle: config.props.textstyle,
    });

    // Layer, colour and lineweight of the note, but not its linetype: a dashed
    // dimension line would read as a construction line.
    this.applyNoteConfigToEntity(entityId, { ignoreLinetype: true });

    const command = new AddEntitiesCommand(this.viewer, {
      entityHandles: [getEntityHandle(entityId)],
      originalLayerName: config.props.layername ?? "0",
    });
    this.viewer.executeCommand(command);
    trackShape("dimension");

    this.selectCreatedEntity(entityId);
    deleteObj(entityId);
  }

  /**
   * No typed input: a dimension measures existing geometry, so typing its
   * length would only make up a measurement (forcing the text is the way to
   * show another value).
   */
  override getTypedInputSpec(): TypedInputSpec | null {
    return null;
  }

  protected override clearLiveMeasurements(): void {
    this.measureLabels?.clear();
    this.measureLabels = null;
  }

  protected clear(): void {
    super.clear();
    this.measureLabels?.clear();
    this.measureLabels = null;
    this.previewLineIds = [];
  }
}
